import React from 'react';
import {useController} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {
  Button,
  EButtonAppearance,
  LoadingButton,
  TButtonProps,
  showNewNotification,
} from '@edna/components';
import {Icon, Stack} from '@edna/components/primitives';

import {Error} from 'src/components/primitives';
import {OkIcon, TrashIcon, UploadIcon} from 'src/icons';

import * as S from './style';

type TProps = {
  name: string;
  label?: React.ReactNode | string;
  onChange?: (value: File) => void;
  onUpload?: (value: File) => void;
  onDelete?: (id: TFile['id']) => void;
  onChangeMultiple?: (value: File[]) => void;
  onUploadMultiple?: (value: File[]) => void;
  accept?: string;
  // max file size in bytes
  maxSize?: number;
  files?: TFile | TFile[] | null;
  isLoading?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  error?: string;
  size?: TButtonProps['size'];
  appearance?: keyof typeof EButtonAppearance;
  multiple?: boolean;
};

const FilePickerField = React.memo<TProps & {name: string}>(
  ({
    name,
    readOnly,
    disabled,
    error,
    onChange,
    onChangeMultiple,
    className,
    isLoading,
    onClick,
    onUpload,
    onDelete,
    onUploadMultiple,
    accept,
    size,
    appearance,
    label,
    files,
    multiple,
    maxSize,
  }) => {
    const {t} = useTranslation();
    const acceptExtensions = accept?.replace(/ |((?<!\w)\.)/g, '').split(',') ?? [];

    const {
      field,
      fieldState: {error: fieldError},
    } = useController({name});

    const value = field.value;

    const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
      field.onBlur();
      onClick?.(event);
    };

    const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
      const targetFiles = event.target.files ? [...event.target.files] : [];

      if (!targetFiles?.length) {
        return;
      }

      const extensions = targetFiles.map((targetFile) => targetFile.name.split('.').pop() ?? '');

      event.target.value = '';

      if (accept && extensions.some((extension) => !acceptExtensions.includes(extension))) {
        showNewNotification({
          type: 'ERROR',
          message: t('FilePicker:invalidFileFormat', {
            formats: acceptExtensions.join(', ').toUpperCase(),
          }),
        });

        return;
      }

      if (maxSize && targetFiles.some((file) => file.size > maxSize)) {
        showNewNotification({
          type: 'ERROR',
          message: t('FilePicker:invalidFileSize', {
            size: Math.round(maxSize / 1024 / 1024),
          }),
        });

        return;
      }

      if (onUploadMultiple) {
        await onUploadMultiple?.(targetFiles);
      } else if (onUpload) {
        await onUpload?.(targetFiles[0]);
      } else if (multiple) {
        field.onChange(targetFiles);
      } else {
        field.onChange(targetFiles[0]);
      }

      onChange?.(targetFiles[0]);
      onChangeMultiple?.(targetFiles);

      field.onBlur();
      event.target.value = '';
    };

    const isFieldInvalid = !disabled && !readOnly && (!!fieldError || !!error);
    const fieldErrorMessage = t(fieldError?.message ?? '', fieldError?.ref?.value);

    const fileLabel = React.useMemo(() => {
      if (label) {
        return label;
      }

      const localePrefix = onUpload ? 'upload' : 'attach';

      if (value) {
        return t(`FilePicker:${localePrefix}AnotherFile`);
      }

      return acceptExtensions.length === 1
        ? t(`FilePicker:${localePrefix}FileWithExtension`, {
            extension: acceptExtensions[0].toUpperCase(),
          })
        : t(`FilePicker:${localePrefix}File`);
    }, [value, label, acceptExtensions, onUpload]);

    const mappedFiles: TFile[] = React.useMemo(() => {
      if (Array.isArray(files)) {
        return files;
      }

      if (files) {
        return [files];
      }

      if (value?.name) {
        return [{originalFileName: value.name, fileUrl: '', id: -1, size: 0}];
      }

      return [];
    }, [value, files]);

    return (
      <Stack size="2" className={className}>
        <Stack size="1">
          {mappedFiles.map((mappedFile) => (
            <Stack size="2" direction="ROW" align="center" key={mappedFile.id}>
              <Icon as={OkIcon} size="20px" color="green500" />
              <S.Link href={mappedFile.fileUrl} target="_blank">
                {mappedFile.originalFileName}
              </S.Link>
              {onDelete && !readOnly && (
                <Button size="S" square onClick={() => onDelete(mappedFile.id)}>
                  <Icon as={TrashIcon} size="12px" />
                </Button>
              )}
            </Stack>
          ))}
        </Stack>
        {!readOnly && (
          <LoadingButton
            as="label"
            // @ts-expect-error prop from as
            htmlFor={name}
            loading={isLoading}
            size={size}
            appearance={appearance}
            disabled={disabled}
          >
            <input
              hidden
              id={name}
              type="file"
              accept={accept}
              onChange={handleChange}
              disabled={disabled || isLoading}
              multiple={multiple}
              onClick={handleClick}
            />
            <Stack size="2" direction="ROW" align="center">
              <Icon as={UploadIcon} size="20px" />
              {fileLabel}
            </Stack>
          </LoadingButton>
        )}
        {isFieldInvalid && <Error>{error ?? (fieldErrorMessage as string)}</Error>}
      </Stack>
    );
  },
);

export {FilePickerField};
